import { useMemo, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MATERIALIDAD_PERIOD_MODE_EJERCICIO,
  MATERIALIDAD_PERIOD_MODE_MES,
  MATERIALIDAD_PERIOD_MODE_RANGO,
  currentPeriodKey,
  defaultPeriodSelection,
  formatPeriodSelectionLabel,
} from "@/modules/tramites/materialidad/materialidadConstants";

function monthStartDate(periodKey) {
  if (!periodKey || periodKey.length !== 7) return new Date();
  return parseISO(`${periodKey}-01`);
}

function dateToPeriodKey(date) {
  if (!date) return currentPeriodKey();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function MonthPicker({ label, value, onChange, testId, compact }) {
  const [open, setOpen] = useState(false);
  const selected = monthStartDate(value);
  return (
    <div className={compact ? "flex items-center gap-1.5 min-w-0" : "space-y-1"}>
      {label ? (
        <Label className={`text-xs shrink-0 ${compact ? "text-slate-500" : ""}`}>{label}</Label>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={`${compact ? "h-8 w-[148px] px-2" : "w-full"} justify-start rounded-sm font-normal text-left`}
            data-testid={testId}
          >
            <CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate capitalize">{format(selected, "MMM yyyy", { locale: es })}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(date) => {
              if (!date) return;
              onChange(dateToPeriodKey(date));
              setOpen(false);
            }}
            locale={es}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Selector de periodo con calendario: mes único, rango o ejercicio fiscal.
 * compact=true → una sola fila horizontal (toolbar de 2 renglones).
 */
export default function MaterialidadPeriodFilter({
  value,
  onChange,
  idPrefix = "materialidad-period",
  compact = false,
}) {
  const selection = value || defaultPeriodSelection();
  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => y - 3 + i);
  }, []);

  const patch = (partial) => onChange?.({ ...selection, ...partial });

  const applyEjercicio = (year, toDate = false) => {
    const y = String(year);
    const from = `${y}-01`;
    const to = toDate ? currentPeriodKey() : `${y}-12`;
    if (parseInt(y, 10) === new Date().getFullYear() && toDate) {
      patch({
        mode: MATERIALIDAD_PERIOD_MODE_RANGO,
        periodFrom: from,
        periodTo: to,
        period: null,
        exerciseYear: parseInt(y, 10),
      });
    } else {
      patch({
        mode: MATERIALIDAD_PERIOD_MODE_EJERCICIO,
        exerciseYear: parseInt(y, 10),
        periodFrom: from,
        periodTo: to,
        period: null,
      });
    }
  };

  const onModeChange = (mode) => {
    if (mode === MATERIALIDAD_PERIOD_MODE_MES) {
      patch({ mode, period: selection.period || currentPeriodKey(), periodFrom: null, periodTo: null });
    } else if (mode === MATERIALIDAD_PERIOD_MODE_RANGO) {
      const now = currentPeriodKey();
      patch({
        mode,
        period: null,
        periodFrom: selection.periodFrom || `${now.slice(0, 4)}-01`,
        periodTo: selection.periodTo || now,
      });
    } else {
      applyEjercicio(selection.exerciseYear || new Date().getFullYear(), false);
    }
  };

  if (compact) {
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0"
        data-testid={`${idPrefix}-filter`}
      >
        <div className="flex items-center gap-1.5">
          <Label htmlFor={`${idPrefix}-mode`} className="text-xs text-slate-500 shrink-0">
            Periodo
          </Label>
          <Select value={selection.mode} onValueChange={onModeChange}>
            <SelectTrigger id={`${idPrefix}-mode`} className="rounded-sm h-8 w-[168px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MATERIALIDAD_PERIOD_MODE_MES}>Mes</SelectItem>
              <SelectItem value={MATERIALIDAD_PERIOD_MODE_RANGO}>Rango</SelectItem>
              <SelectItem value={MATERIALIDAD_PERIOD_MODE_EJERCICIO}>Ejercicio</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selection.mode === MATERIALIDAD_PERIOD_MODE_MES ? (
          <MonthPicker
            label="Mes"
            value={selection.period || currentPeriodKey()}
            onChange={(period) => patch({ period, periodFrom: null, periodTo: null })}
            testId={`${idPrefix}-month`}
            compact
          />
        ) : null}

        {selection.mode === MATERIALIDAD_PERIOD_MODE_RANGO ? (
          <>
            <MonthPicker
              label="Desde"
              value={selection.periodFrom || currentPeriodKey()}
              onChange={(periodFrom) => patch({ periodFrom })}
              testId={`${idPrefix}-from`}
              compact
            />
            <MonthPicker
              label="Hasta"
              value={selection.periodTo || currentPeriodKey()}
              onChange={(periodTo) => patch({ periodTo })}
              testId={`${idPrefix}-to`}
              compact
            />
          </>
        ) : null}

        {selection.mode === MATERIALIDAD_PERIOD_MODE_EJERCICIO ? (
          <>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-slate-500 shrink-0">Año</Label>
              <Select
                value={String(selection.exerciseYear || new Date().getFullYear())}
                onValueChange={(y) => applyEjercicio(parseInt(y, 10), false)}
              >
                <SelectTrigger className="rounded-sm h-8 w-[96px]" data-testid={`${idPrefix}-year`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs rounded-sm text-[#0F2D5C]"
              onClick={() => applyEjercicio(selection.exerciseYear || new Date().getFullYear(), true)}
              data-testid={`${idPrefix}-ytd`}
            >
              Enero → actual
            </Button>
          </>
        ) : null}

        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {formatPeriodSelectionLabel(selection)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2 min-w-[220px]" data-testid={`${idPrefix}-filter`}>
      <Label htmlFor={`${idPrefix}-mode`}>Periodo</Label>
      <Select value={selection.mode} onValueChange={onModeChange}>
        <SelectTrigger id={`${idPrefix}-mode`} className="rounded-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={MATERIALIDAD_PERIOD_MODE_MES}>Mes</SelectItem>
          <SelectItem value={MATERIALIDAD_PERIOD_MODE_RANGO}>Rango (inicio – fin)</SelectItem>
          <SelectItem value={MATERIALIDAD_PERIOD_MODE_EJERCICIO}>Ejercicio (año completo)</SelectItem>
        </SelectContent>
      </Select>

      {selection.mode === MATERIALIDAD_PERIOD_MODE_MES ? (
        <MonthPicker
          label="Mes"
          value={selection.period || currentPeriodKey()}
          onChange={(period) => patch({ period, periodFrom: null, periodTo: null })}
          testId={`${idPrefix}-month`}
        />
      ) : null}

      {selection.mode === MATERIALIDAD_PERIOD_MODE_RANGO ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <MonthPicker
            label="Desde"
            value={selection.periodFrom || currentPeriodKey()}
            onChange={(periodFrom) => patch({ periodFrom })}
            testId={`${idPrefix}-from`}
          />
          <MonthPicker
            label="Hasta"
            value={selection.periodTo || currentPeriodKey()}
            onChange={(periodTo) => patch({ periodTo })}
            testId={`${idPrefix}-to`}
          />
        </div>
      ) : null}

      {selection.mode === MATERIALIDAD_PERIOD_MODE_EJERCICIO ? (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">Año fiscal</Label>
            <Select
              value={String(selection.exerciseYear || new Date().getFullYear())}
              onValueChange={(y) => applyEjercicio(parseInt(y, 10), false)}
            >
              <SelectTrigger className="rounded-sm" data-testid={`${idPrefix}-year`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs rounded-sm text-[#0F2D5C]"
            onClick={() => applyEjercicio(selection.exerciseYear || new Date().getFullYear(), true)}
            data-testid={`${idPrefix}-ytd`}
          >
            Enero → mes actual
          </Button>
        </div>
      ) : null}

      <p className="text-[11px] text-slate-500 m-0">
        {formatPeriodSelectionLabel(selection)}
      </p>
    </div>
  );
}
